import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Sun, Moon } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/services", label: "Services", testid: "nav-services" },
  { to: "/lets-build", label: "Let's build", testid: "nav-lets-build" },
  { to: "/tools", label: "Tools", testid: "nav-tools" },
  { to: "/who-we-are", label: "Who we are", testid: "nav-who-we-are" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  return (
    <header className="sticky top-0 z-50 border-b border-zinc-200 bg-white/80 backdrop-blur-xl">
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img src="/genesis-mark-v2.png" alt="Genesis IT Solution logo" className="h-11 w-auto shrink-0 transition-transform duration-300 group-hover:scale-105" />
          <div className="flex flex-col leading-none">
            <span className="font-heading text-xl font-black tracking-tight text-zinc-900">GENESIS</span>
            <span className="font-mono-label text-[9px] text-klein mt-1">IT Solution</span>
          </div>
        </Link>

        <div className="hidden items-center gap-1 md:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.testid}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium transition-colors ${
                  isActive ? "text-klein" : "text-zinc-600 hover:text-zinc-900"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle"
            aria-label="Toggle dark mode"
            className="ml-2 flex h-9 w-9 items-center justify-center border border-zinc-200 text-zinc-700 transition-colors hover:bg-zinc-50"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <Link
            to="/lets-build"
            data-testid="nav-cta"
            className="ml-3 bg-klein px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-kleindark"
          >
            Start a project
          </Link>
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-mobile"
            aria-label="Toggle dark mode"
            className="flex h-9 w-9 items-center justify-center border border-zinc-200 text-zinc-700"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="border-t border-zinc-200 bg-white md:hidden">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`${l.testid}-mobile`}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block border-b border-zinc-100 px-6 py-4 text-sm font-medium ${
                  isActive ? "text-klein" : "text-zinc-700"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>
      )}
    </header>
  );
}
